import { notifications } from "@marketplace/ui/notifications";
import {
  useMutation,
  type UseMutationOptions,
  useQueryClient
} from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { DeleteSalesRequestDto } from "../../dtos/delete-sales-request-dto";
import type { DeleteSalesResponseDto } from "../../dtos/delete-sales-response-dto";
import type { GetSalesListResponseDto } from "../../dtos/get-sales-list-response-dto";
import { UserQueryKeys } from "../../generators/user-query-keys";
import { UserService } from "../../services/user-service";

export type UseDeleteSalesMutationParams = {
  options?: UseMutationOptions<
    DeleteSalesResponseDto,
    unknown,
    DeleteSalesRequestDto
  >;
};

export const useDeleteSalesMutation = ({
  options
}: UseDeleteSalesMutationParams = {}) => {
  const queryClient = useQueryClient();
  const { q, page, sort } = useSearch({ from: "/users/sales/" });
  const navigate = useNavigate();
  const { t } = useTranslation("users", {
    keyPrefix: "createSalesPage.notifications"
  });

  return useMutation({
    mutationFn: async (payload) => {
      return await UserService.deleteSales({
        payload: {
          salesId: payload.salesId
        }
      });
    },
    onSuccess: async (...args) => {
      const currentPageKey = UserQueryKeys.getSalesList({
        limit: 10,
        page,
        q,
        sort
      }).queryKey;

      // Invalidate and refetch current page
      await queryClient.invalidateQueries({ queryKey: currentPageKey });
      const newData = (
        queryClient.getQueryData(currentPageKey) as GetSalesListResponseDto
      ).data;

      // If the page is empty and it's not the first page, go back one page
      if (Array.isArray(newData) && newData.length === 0 && page > 1) {
        const previousPageKey = UserQueryKeys.getSalesList({
          limit: 10,
          page: page - 1,
          q,
          sort
        }).queryKey;
        await queryClient.invalidateQueries({ queryKey: previousPageKey });

        navigate({
          to: "/users/sales",
          search: {
            q,
            page: page - 1
          }
        });
      }

      // Show success notification
      notifications.show({
        message: t("deleteSuccess")
      });

      options?.onSuccess?.(...args);
    },
    onError: (...errorArgs) => {
      // Show error notification
      notifications.show({
        variant: "error",
        message: t("deleteFailed")
      });

      options?.onError?.(...errorArgs);
    },
    ...options
  });
};
